import { signal, Signal, WritableSignal } from '@angular/core';
import * as THREE from 'three';

export interface ActionArgs {
  icon?: string;
  tooltip?: string;
  onclick?: (event: any) => void;
}

export class Action {
  icon: string;
  tooltip: string | undefined;

  constructor(args: ActionArgs = {}) {
    this.icon = args.icon || '';
    this.tooltip = args.tooltip;

    if (args.onclick !== undefined) {
      this.onclick = args.onclick;
    }
  }

  onclick(_event: any): void {}

  pairWith(_object: any): void {}
}

export interface AnnotationArgs {
  title?: string;
  description?: string;
  position?: THREE.Vector3 | number[] | null;
  cameraPosition?: THREE.Vector3 | number[];
  cameraTarget?: THREE.Vector3 | number[];
  radius?: number;
  view?: any;
  actions?: (Action | ActionArgs)[];
  collapseThreshold?: number;
}

export class Annotation {
  scene: any = null;
  _title: string;
  _description: string;
  offset = new THREE.Vector3();
  uuid = THREE.MathUtils.generateUUID();
  position: THREE.Vector3 | null;
  cameraPosition: THREE.Vector3 | undefined;
  cameraTarget: THREE.Vector3 | undefined;
  radius: number | undefined;
  view: any;
  keepOpen = false;
  descriptionVisible = false;
  showDescription = true;
  actions: Action[];
  isHighlighted = false;
  _visible = true;
  __visible = true;
  _display = false;
  _expand = false;
  collapseThreshold: number;
  children: Annotation[] = [];
  parent: Annotation | null = null;
  boundingBox = new THREE.Box3();

  /** Signal emitting the last added child annotation. */
  readonly childAdded: Signal<Annotation | undefined>;
  private _childAdded: WritableSignal<Annotation | undefined>;

  /** Signal emitting the last removed child annotation. */
  readonly childRemoved: Signal<Annotation | undefined>;
  private _childRemoved: WritableSignal<Annotation | undefined>;

  constructor(args: AnnotationArgs = {}) {
    this._childAdded = signal<Annotation | undefined>(undefined);
    this.childAdded = this._childAdded.asReadonly();
    this._childRemoved = signal<Annotation | undefined>(undefined);
    this.childRemoved = this._childRemoved.asReadonly();

    this._title = args.title || 'No Title';
    this._description = args.description || '';

    if (!args.position) {
      this.position = null;
    } else if ((args.position as THREE.Vector3).x != null) {
      this.position = args.position as THREE.Vector3;
    } else {
      this.position = new THREE.Vector3(...(args.position as number[]));
    }

    this.cameraPosition =
      args.cameraPosition instanceof Array
        ? new THREE.Vector3().fromArray(args.cameraPosition)
        : args.cameraPosition;
    this.cameraTarget =
      args.cameraTarget instanceof Array
        ? new THREE.Vector3().fromArray(args.cameraTarget)
        : args.cameraTarget;
    this.radius = args.radius;
    this.view = args.view || null;
    this.collapseThreshold = [args.collapseThreshold, 100].find(e => e !== undefined)!;

    this.actions = (args.actions || []).map(a => {
      if (a instanceof Action) {
        return a;
      } else {
        return new Action(a);
      }
    });

    for (const action of this.actions) {
      action.pairWith(this);
    }
  }

  add(annotation: Annotation): void {
    if (!this.children.includes(annotation)) {
      this.children.push(annotation);
      annotation.parent = this;
      this._childAdded.set(annotation);
    }
  }

  remove(annotation: Annotation): void {
    const index = this.children.indexOf(annotation);
    if (index >= 0) {
      this.children.splice(index, 1);
      annotation.parent = null;
      this._childRemoved.set(annotation);
    }
  }

  traverse(handler: (annotation: Annotation) => boolean | undefined | void): void {
    const expand = handler(this);

    if (expand === undefined || expand === true) {
      for (const child of this.children) {
        child.traverse(handler);
      }
    }
  }

  traverseDescendants(handler: (annotation: Annotation) => boolean | undefined | void): void {
    for (const child of this.children) {
      child.traverse(handler);
    }
  }

  flatten(): Annotation[] {
    const annotations: Annotation[] = [];
    this.traverse(annotation => {
      annotations.push(annotation);
    });
    return annotations;
  }

  descendants(): Annotation[] {
    const annotations: Annotation[] = [];
    this.traverse(annotation => {
      if (annotation !== this) {
        annotations.push(annotation);
      }
    });
    return annotations;
  }

  hasView(): boolean {
    let hasPosTargetView = this.cameraTarget?.x != null;
    hasPosTargetView = hasPosTargetView && this.cameraPosition?.x != null;

    const hasRadiusView = this.radius !== undefined;

    return hasPosTargetView || hasRadiusView;
  }

  toString(): string {
    return 'Annotation: ' + this._title;
  }
}
